import apiClient from '@/lib/apiClient';
import {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  PaginatedResponse,
  ApiResponse,
} from '@/types/api';

/**
 * Product API endpoints
 */
export const productApi = {
  /**
   * Get all products with pagination and filters
   */
  getProducts: async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    category?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Product>> => {
    const response = await apiClient.get<
      ApiResponse<PaginatedResponse<Product>>
    >('/products', {
      params,
    });
    return response.data.data!;
  },

  /**
   * Get single product by ID
   */
  getProduct: async (id: string): Promise<Product> => {
    const response = await apiClient.get<ApiResponse<Product>>(
      `/products/${id}`
    );
    return response.data.data!;
  },

  /**
   * Create new product
   */
  createProduct: async (data: CreateProductRequest): Promise<Product> => {
    const response = await apiClient.post<ApiResponse<Product>>(
      '/products',
      data
    );
    return response.data.data!;
  },

  /**
   * Update existing product
   */
  updateProduct: async (
    id: string,
    data: UpdateProductRequest
  ): Promise<Product> => {
    const response = await apiClient.patch<ApiResponse<Product>>(
      `/products/${id}`,
      data
    );
    return response.data.data!;
  },

  /**
   * Delete product
   */
  deleteProduct: async (id: string): Promise<void> => {
    await apiClient.delete(`/products/${id}`);
  },

  /**
   * Bulk update products
   */
  bulkUpdate: async (
    ids: string[],
    data: Partial<UpdateProductRequest>
  ): Promise<{ updated: number }> => {
    const response = await apiClient.patch<ApiResponse<{ updated: number }>>(
      '/products/bulk',
      {
        ids,
        data,
      }
    );
    return response.data.data!;
  },

  /**
   * Bulk delete products
   */
  bulkDelete: async (ids: string[]): Promise<{ deleted: number }> => {
    const response = await apiClient.post<ApiResponse<{ deleted: number }>>(
      '/products/bulk-delete',
      {
        ids,
      }
    );
    return response.data.data!;
  },

  /**
   * Upload product images
   */
  uploadImages: async (
    productId: string,
    files: File[]
  ): Promise<{ urls: string[] }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('images', file);
    });

    const response = await apiClient.post<ApiResponse<{ urls: string[] }>>(
      `/products/${productId}/images`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data!;
  },

  /**
   * Delete product image
   */
  deleteImage: async (productId: string, imageUrl: string): Promise<void> => {
    await apiClient.delete(`/products/${productId}/images`, {
      data: { imageUrl },
    });
  },
};
